import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentStatus() {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({ kind: "polling", message: "Verifying your payment..." });
  const [details, setDetails] = useState(null);
  const polledRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get("payment");
    const unlock = params.get("unlock");
    const sessionId = params.get("session_id");
    const profileId = params.get("profile_id");

    if (payment === "cancel" || unlock === "cancel") {
      setState({ kind: "cancel", message: unlock === "cancel"
        ? "Unlock cancelled. Your blueprint stays right where you left it."
        : "Payment was cancelled. You can try again at any time." });
      setOpen(true);
      cleanUrl();
      return;
    }
    if (unlock === "success" && sessionId && !polledRef.current) {
      polledRef.current = true;
      setOpen(true);
      setState({ kind: "polling", message: "Unlocking your full blueprint..." });
      pollUnlock(sessionId, profileId, 0);
      return;
    }
    if (payment === "success" && sessionId && !polledRef.current) {
      polledRef.current = true;
      setOpen(true);
      pollPayment(sessionId, 0);
    }
    // pollPayment / pollUnlock only use stable refs and setters — safe to
    // omit them from deps; this effect is intentionally a mount-once hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cleanUrl = () => {
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  const pollPayment = async (sessionId, attempts) => {
    const max = 8;
    if (attempts >= max) {
      setState({ kind: "timeout", message: "Payment status check timed out. Please check your email." });
      cleanUrl();
      return;
    }
    try {
      const r = await axios.get(`${API}/checkout/status/${sessionId}`);
      const { payment_status, status, metadata } = r.data;
      if (payment_status === "paid") {
        setDetails(metadata);
        setState({
          kind: "success",
          message: `Your booking is confirmed for ${metadata?.booking_date || "—"} at ${metadata?.booking_time || "—"}.`,
        });
        cleanUrl();
        return;
      }
      if (status === "expired") {
        setState({ kind: "expired", message: "Payment session expired. Please start over." });
        cleanUrl();
        return;
      }
      setTimeout(() => pollPayment(sessionId, attempts + 1), 2000);
    } catch (e) {
      setState({ kind: "error", message: "Could not verify payment. Please contact support." });
      cleanUrl();
    }
  };

  const pollUnlock = async (sessionId, profileId, attempts) => {
    const max = 12;
    if (attempts >= max) {
      setState({ kind: "timeout", message: "We couldn't confirm your unlock yet. Please refresh in a few seconds." });
      cleanUrl();
      return;
    }
    try {
      const r = await axios.get(`${API}/checkout/status/${sessionId}`);
      const { payment_status, status } = r.data;
      if (payment_status === "paid") {
        setState({
          kind: "success",
          message: "Your full blueprint is now revealed. The veil has lifted — enjoy.",
        });
        cleanUrl();
        // Reload after short delay so the user sees the success state, then sees full content
        setTimeout(() => window.location.reload(), 1400);
        return;
      }
      if (status === "expired") {
        setState({ kind: "expired", message: "Unlock session expired. Please try again." });
        cleanUrl();
        return;
      }
      setTimeout(() => pollUnlock(sessionId, profileId, attempts + 1), 1500);
    } catch (e) {
      setState({ kind: "error", message: "Could not verify the unlock. Please contact support." });
      cleanUrl();
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="payment-status-modal"
        className="max-w-md bg-[#0B0E14] border border-[#D4AF37]/20 text-white"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-white flex items-center gap-3">
            {state.kind === "polling" && <><Loader2 className="w-5 h-5 animate-spin text-[#D4AF37]" /> Verifying</>}
            {state.kind === "success" && <><CheckCircle2 className="w-6 h-6 text-emerald-400" /> Booking confirmed</>}
            {(state.kind === "cancel" || state.kind === "expired" || state.kind === "error" || state.kind === "timeout") && <><XCircle className="w-6 h-6 text-red-300" /> {state.kind === "cancel" ? "Payment cancelled" : "Issue"}</>}
          </DialogTitle>
        </DialogHeader>
        <p className="font-sans text-slate-300 leading-relaxed mt-3">{state.message}</p>
        {details && (
          <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.025] p-4 space-y-1 text-sm font-sans">
            <p><span className="text-[#D4AF37]">Session:</span> {details.package_name}</p>
            <p><span className="text-[#D4AF37]">Date:</span> {details.booking_date} at {details.booking_time}</p>
            <p><span className="text-[#D4AF37]">Format:</span> {details.booking_format === "in_person" ? "In person" : "Online video"}</p>
            <p><span className="text-[#D4AF37]">Name:</span> {details.client_name}</p>
            <p><span className="text-[#D4AF37]">Email:</span> {details.client_email}</p>
            {details.client_email && (
              <p className="pt-2 text-xs text-slate-400" data-testid="confirmation-email-note">
                A confirmation email with all the details has been sent to {details.client_email}.
              </p>
            )}
          </div>
        )}
        <div className="mt-6 flex justify-end">
          <button
            data-testid="payment-modal-close"
            onClick={() => setOpen(false)}
            className="btn-ghost-gold rounded-full px-6 py-2 text-xs"
          >
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
